import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PitchSlide } from '../../data/slides';
import { appTheme } from '../../styles/theme';

const PromptField = styled.div`
  position: relative;
`;

const PromptEditor = styled.div<{ $disabled?: boolean; $borderless?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-height: ${({ $borderless }) => ($borderless ? '44px' : '72px')};
  padding: ${({ $borderless }) => ($borderless ? '4px 0' : '8px 10px')};
  border: ${({ $borderless }) => ($borderless ? 'none' : `1px solid ${appTheme.border}`)};
  border-radius: ${({ $borderless }) => ($borderless ? '0' : appTheme.radiusSm)};
  background: ${({ $borderless }) => ($borderless ? 'transparent' : appTheme.surface)};
  box-sizing: border-box;
  cursor: text;
  opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};

  &:focus-within {
    outline: ${({ $borderless }) => ($borderless ? 'none' : `2px solid ${appTheme.green}`)};
    outline-offset: -1px;
  }
`;

const Placeholder = styled.span<{ $borderless?: boolean }>`
  position: absolute;
  top: ${({ $borderless }) => ($borderless ? '4px' : '8px')};
  left: ${({ $borderless }) => ($borderless ? '0' : '10px')};
  font-size: 0.8125rem;
  color: ${appTheme.textMuted};
  pointer-events: none;
  user-select: none;
`;

const TextSegment = styled.span`
  outline: none;
  min-width: 1ch;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${appTheme.text};
  white-space: pre-wrap;
  word-break: break-word;
`;

const MentionTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 3px 6px 3px 10px;
  border-radius: ${appTheme.radiusPill};
  background: ${appTheme.mint};
  border: 1px solid rgba(19, 78, 68, 0.2);
  color: ${appTheme.green};
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  vertical-align: middle;
`;

const MentionTagLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveTagButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${appTheme.green};
  font-size: 0.875rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(19, 78, 68, 0.12);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const MentionMenu = styled.ul`
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 6px);
  margin: 0;
  padding: 6px;
  list-style: none;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  background: ${appTheme.surface};
  box-shadow: ${appTheme.shadowMd};
  max-height: 200px;
  overflow-y: auto;
  z-index: 20;
`;

const MentionItem = styled.li<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ $active }) => ($active ? appTheme.mint : 'transparent')};

  &:hover {
    background: ${appTheme.mint};
  }
`;

const MentionLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${appTheme.green};
`;

const MentionTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${appTheme.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type PromptPart =
  | { id: string; type: 'text'; value: string }
  | { id: string; type: 'mention'; slideIndex: number };

interface ActiveMention {
  textPartId: string;
  start: number;
  end: number;
  query: string;
}

const createPartId = (): string => Math.random().toString(36).slice(2, 10);

const emptyParts = (): PromptPart[] => [{ id: createPartId(), type: 'text', value: '' }];

export const formatSlideMention = (index: number, slide: PitchSlide): string =>
  `@Slide ${index + 1} (${slide.tabLabel})`;

export const formatSlideMentionLabel = (index: number, slide: PitchSlide): string =>
  `Slide ${index + 1} · ${slide.tabLabel}`;

export const serializePromptParts = (parts: PromptPart[], slides: PitchSlide[]): string =>
  parts
    .map((part) =>
      part.type === 'text'
        ? part.value
        : formatSlideMention(part.slideIndex, slides[part.slideIndex]),
    )
    .join('')
    .trim();

const mergeAdjacentText = (parts: PromptPart[]): PromptPart[] => {
  const merged: PromptPart[] = [];
  for (const part of parts) {
    const last = merged[merged.length - 1];
    if (part.type === 'text' && last?.type === 'text') {
      last.value += part.value;
    } else if (part.type === 'text' || part.type === 'mention') {
      merged.push(part.type === 'text' ? { ...part } : { ...part });
    }
  }
  return merged.length > 0 ? merged : emptyParts();
};

const getActiveMention = (text: string, cursor: number): Omit<ActiveMention, 'textPartId'> | null => {
  const before = text.slice(0, cursor);
  const atIndex = before.lastIndexOf('@');
  if (atIndex === -1) {
    return null;
  }
  const query = before.slice(atIndex + 1);
  if (/\s/.test(query)) {
    return null;
  }
  return { start: atIndex, end: cursor, query: query.toLowerCase() };
};

const getCaretOffset = (element: HTMLElement): number => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return element.textContent?.length ?? 0;
  }
  const range = selection.getRangeAt(0);
  if (!element.contains(range.startContainer)) {
    return element.textContent?.length ?? 0;
  }
  const preRange = range.cloneRange();
  preRange.selectNodeContents(element);
  preRange.setEnd(range.startContainer, range.startOffset);
  return preRange.toString().length;
};

const setCaretOffset = (element: HTMLElement, offset: number): void => {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let node = walker.nextNode() as Text | null;

  while (node) {
    const length = node.textContent?.length ?? 0;
    if (remaining <= length) {
      const range = document.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= length;
    node = walker.nextNode() as Text | null;
  }

  const range = document.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
};

interface EditableTextSegmentProps {
  partId: string;
  value: string;
  disabled: boolean;
  promptAria: string;
  registerRef: (partId: string, element: HTMLSpanElement | null) => void;
  onChange: (partId: string, value: string, cursor: number) => void;
  onBlur: () => void;
}

const EditableTextSegment: React.FC<EditableTextSegmentProps> = ({
  partId,
  value,
  disabled,
  promptAria,
  registerRef,
  onChange,
  onBlur,
}) => {
  const elementRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    registerRef(partId, elementRef.current);
    return () => registerRef(partId, null);
  }, [partId, registerRef]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || document.activeElement === element) {
      return;
    }
    if (element.textContent !== value) {
      element.textContent = value;
    }
  }, [partId, value]);

  return (
    <TextSegment
      ref={elementRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      role="textbox"
      aria-label={promptAria}
      onInput={(event) => {
        const element = event.currentTarget;
        onChange(partId, element.textContent ?? '', getCaretOffset(element));
      }}
      onKeyUp={(event) => {
        const element = event.currentTarget;
        onChange(partId, element.textContent ?? '', getCaretOffset(element));
      }}
      onClick={(event) => {
        const element = event.currentTarget;
        onChange(partId, element.textContent ?? '', getCaretOffset(element));
      }}
      onBlur={onBlur}
    />
  );
};

interface MentionPromptEditorProps {
  slides: PitchSlide[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  borderless?: boolean;
  placeholder?: string;
  onSubmit?: () => void;
  submitDisabled?: boolean;
}

const MentionPromptEditor: React.FC<MentionPromptEditorProps> = ({
  slides,
  value,
  onChange,
  disabled = false,
  borderless = false,
  placeholder,
  onSubmit,
  submitDisabled = false,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('editor.ai.placeholder');
  const [parts, setParts] = useState<PromptPart[]>(emptyParts);
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const textRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const pendingFocusPartId = useRef<string | null>(null);
  const pendingCaretOffset = useRef<number | null>(null);

  const isEmpty =
    parts.length === 1 &&
    parts[0].type === 'text' &&
    parts[0].value === '' &&
    !parts.some((part) => part.type === 'mention');

  const formatMentionLabel = (index: number, slide: PitchSlide): string =>
    t('editor.mention.slideLabel', { index: index + 1, tabLabel: slide.tabLabel });

  const registerTextRef = useCallback((partId: string, element: HTMLSpanElement | null) => {
    textRefs.current[partId] = element;
  }, []);

  const updateParts = useCallback(
    (nextParts: PromptPart[]) => {
      const merged = mergeAdjacentText(nextParts);
      setParts(merged);
      onChange(serializePromptParts(merged, slides));
    },
    [onChange, slides],
  );

  useEffect(() => {
    if (value === '') {
      setParts(emptyParts());
      setActiveMention(null);
    }
  }, [value]);

  useEffect(() => {
    setMentionIndex(0);
  }, [activeMention?.query, activeMention?.start, activeMention?.textPartId]);

  useEffect(() => {
    const partId = pendingFocusPartId.current;
    if (!partId) {
      return;
    }
    pendingFocusPartId.current = null;
    const element = textRefs.current[partId];
    if (!element) {
      return;
    }
    element.focus();
    const offset = pendingCaretOffset.current ?? element.textContent?.length ?? 0;
    pendingCaretOffset.current = null;
    requestAnimationFrame(() => setCaretOffset(element, offset));
  }, [parts]);

  const mentionOptions = useMemo(() => {
    if (!activeMention) {
      return [];
    }
    return slides
      .map((slide, index) => ({ slide, index }))
      .filter(({ slide, index }) => {
        if (!activeMention.query) {
          return true;
        }
        const haystack = `${index + 1} ${slide.tabLabel} ${slide.title} ${slide.id}`.toLowerCase();
        return haystack.includes(activeMention.query);
      });
  }, [activeMention, slides]);

  const closeMention = useCallback(() => {
    setActiveMention(null);
    setMentionIndex(0);
  }, []);

  const handleTextChange = useCallback(
    (textPartId: string, text: string, cursor: number) => {
      setParts((current) => {
        const nextParts = current.map((part) =>
          part.id === textPartId && part.type === 'text' ? { ...part, value: text } : part,
        );
        onChange(serializePromptParts(nextParts, slides));
        return nextParts;
      });

      const mention = getActiveMention(text, cursor);
      if (!mention) {
        closeMention();
        return;
      }
      setActiveMention({ textPartId, ...mention });
    },
    [closeMention, onChange, slides],
  );

  const insertMention = useCallback(
    (slideIndex: number) => {
      if (!activeMention) {
        return;
      }

      const textPart = parts.find(
        (part): part is Extract<PromptPart, { type: 'text' }> =>
          part.id === activeMention.textPartId && part.type === 'text',
      );
      if (!textPart) {
        return;
      }

      const before = textPart.value.slice(0, activeMention.start);
      const after = textPart.value.slice(activeMention.end);
      const partIndex = parts.findIndex((part) => part.id === textPart.id);
      const afterTextId = createPartId();

      const nextParts: PromptPart[] = [
        ...parts.slice(0, partIndex),
        ...(before ? [{ id: createPartId(), type: 'text' as const, value: before }] : []),
        { id: createPartId(), type: 'mention' as const, slideIndex },
        {
          id: afterTextId,
          type: 'text' as const,
          value: after ? (after.startsWith(' ') ? after : ` ${after}`) : ' ',
        },
        ...parts.slice(partIndex + 1),
      ];

      pendingFocusPartId.current = afterTextId;
      pendingCaretOffset.current = 1;
      updateParts(nextParts);
      closeMention();
    },
    [activeMention, closeMention, parts, updateParts],
  );

  const removeMention = (mentionPartId: string) => {
    updateParts(parts.filter((part) => part.id !== mentionPartId));
  };

  const focusLastTextPart = () => {
    const lastText = [...parts].reverse().find((part) => part.type === 'text');
    if (lastText?.type === 'text') {
      textRefs.current[lastText.id]?.focus();
    }
  };

  const handleEditorKeyDown = (event: React.KeyboardEvent) => {
    if (activeMention && mentionOptions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionIndex((current) => (current + 1) % mentionOptions.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionIndex((current) => (current - 1 + mentionOptions.length) % mentionOptions.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertMention(mentionOptions[mentionIndex].index);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMention();
      }
      return;
    }

    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      onSubmit &&
      !disabled &&
      !submitDisabled
    ) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <PromptField>
      {activeMention && mentionOptions.length > 0 && (
        <MentionMenu role="listbox" aria-label={t('editor.ai.referenceSlide')}>
          {mentionOptions.map(({ slide, index }, optionIndex) => (
            <MentionItem
              key={slide.id}
              role="option"
              aria-selected={optionIndex === mentionIndex}
              $active={optionIndex === mentionIndex}
              onMouseDown={(event) => {
                event.preventDefault();
                insertMention(index);
              }}
            >
              <MentionLabel>{formatMentionLabel(index, slide)}</MentionLabel>
              <MentionTitle>{slide.title}</MentionTitle>
            </MentionItem>
          ))}
        </MentionMenu>
      )}

      <PromptEditor
        ref={editorRef}
        $disabled={disabled}
        $borderless={borderless}
        onKeyDown={handleEditorKeyDown}
        onMouseDown={(event) => {
          if (disabled) {
            return;
          }
          if (event.target === editorRef.current) {
            event.preventDefault();
            focusLastTextPart();
          }
        }}
      >
        {isEmpty && <Placeholder $borderless={borderless}>{resolvedPlaceholder}</Placeholder>}
        {parts.map((part) => {
          if (part.type === 'mention') {
            const slide = slides[part.slideIndex];
            if (!slide) {
              return null;
            }
            const mentionLabel = formatMentionLabel(part.slideIndex, slide);
            return (
              <MentionTag key={part.id} contentEditable={false}>
                <MentionTagLabel>{mentionLabel}</MentionTagLabel>
                <RemoveTagButton
                  type="button"
                  aria-label={t('editor.ai.removeReference', { label: mentionLabel })}
                  disabled={disabled}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => removeMention(part.id)}
                >
                  ×
                </RemoveTagButton>
              </MentionTag>
            );
          }

          return (
            <EditableTextSegment
              key={part.id}
              partId={part.id}
              value={part.value}
              disabled={disabled}
              promptAria={t('editor.ai.promptAria')}
              registerRef={registerTextRef}
              onChange={handleTextChange}
              onBlur={() => {
                window.setTimeout(closeMention, 120);
              }}
            />
          );
        })}
      </PromptEditor>
    </PromptField>
  );
};

export default MentionPromptEditor;
